'use client';

import { useMemo } from 'react';

import dynamic from 'next/dynamic';

import { ThemeProvider } from 'next-themes';

import { CaptchaProvider } from '@kit/auth/captcha/client';
import { I18nProvider } from '@kit/i18n/provider';
import { MonitoringProvider } from '@kit/monitoring/components';
import { AppEventsProvider } from '@kit/shared/events';
import { If } from '@kit/ui/if';
import { TooltipProvider } from '@kit/ui/tooltip';
import { VersionUpdater } from '@kit/ui/version-updater';

import { AnalyticsProvider } from '~/components/analytics-provider';
import { AuthProvider } from '~/components/auth-provider';
import { HydrationMarker } from '~/components/hydration-marker';
import appConfig from '~/config/app.config';
import authConfig from '~/config/auth.config';
import featuresFlagConfig from '~/config/feature-flags.config';
import { NrfGlobalAudioPlayerProvider } from '~/home/(user)/_lib/nrf-global-audio-player-context';
import { i18nResolver } from '~/lib/i18n/i18n.resolver';
import { getI18nSettings } from '~/lib/i18n/i18n.settings';

import { NavigationBlockerProvider } from './navigation-blocker';
import { CreditProvider } from './nrf-credits-context';
import { NrfFullscreenLoadingProvider } from './nrf-fullscreen-loading-context';
import { ReactQueryProvider } from './react-query-provider';

export function RootProviders({
  lang,
  theme = appConfig.theme,
  children,
}: React.PropsWithChildren<{
  lang: string;
  theme?: string;
}>) {
  const i18nSettings = useMemo(() => getI18nSettings(lang), [lang]);

  return (
    <MonitoringProvider>
      <AppEventsProvider>
        <AnalyticsProvider>
          <ReactQueryProvider>
            <I18nProvider settings={i18nSettings} resolver={i18nResolver}>
              <NrfFullscreenLoadingProvider>
                <TooltipProvider>
                  <CaptchaProvider>
                    <AuthProvider>
                      <CreditProvider>
                        <NrfGlobalAudioPlayerProvider>
                          <NavigationBlockerProvider>
                            <ThemeProvider
                              attribute="class"
                              enableSystem
                              disableTransitionOnChange
                              defaultTheme={theme}
                              enableColorScheme={false}
                            >
                              {/* Keep marker inside the hydrated app tree (after i18n/provider suspense). */}
                              <HydrationMarker />
                              {children}
                            </ThemeProvider>
                          </NavigationBlockerProvider>
                        </NrfGlobalAudioPlayerProvider>
                      </CreditProvider>
                    </AuthProvider>
                  </CaptchaProvider>
                </TooltipProvider>

                <If condition={featuresFlagConfig.enableVersionUpdater}>
                  <VersionUpdater />
                </If>
              </NrfFullscreenLoadingProvider>
            </I18nProvider>
          </ReactQueryProvider>
        </AnalyticsProvider>
      </AppEventsProvider>
    </MonitoringProvider>
  );
}
